import React from 'react';
import {
	Globe,
	Share2,
	Music,
	MessageCircle,
	Send,
	Disc,
	Utensils,
	ShoppingBag,
	Radio,
	Sparkles,
	Link2,
} from 'lucide-react';
import type { TExperienceSocialPlatform } from '@shared/schemas';

export interface SocialPlatformMeta {
	id: TExperienceSocialPlatform;
	name: string;
	defaultLabel: string;
	urlPlaceholder: string;
	urlPrefix?: string;
	category: 'social' | 'messaging' | 'media' | 'dev' | 'reviews_food' | 'other';
	iconBg: string;
	renderIcon: (className?: string) => React.ReactNode;
	formatUrl: (raw: string) => string;
}

export const SOCIAL_PLATFORMS_REGISTRY: SocialPlatformMeta[] = [
	{
		id: 'website',
		name: 'Website',
		defaultLabel: 'Official Website',
		urlPlaceholder: 'https://example.com',
		category: 'other',
		iconBg: 'bg-slate-900 text-white',
		renderIcon: (cls = 'size-4') => <Globe className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://'),
	},
	{
		id: 'linkedin',
		name: 'LinkedIn',
		defaultLabel: 'LinkedIn',
		urlPlaceholder: 'https://linkedin.com/in/username or username',
		urlPrefix: 'https://www.linkedin.com/in/',
		category: 'social',
		iconBg: 'bg-[#0077b5] text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 8.76a1.6 1.6 0 0 0 1.6-1.6 1.6 1.6 0 0 0-1.6-1.6 1.6 1.6 0 0 0-1.6 1.6c0 .88.72 1.6 1.6 1.6m1.4 9.74v-8.37H5.06v8.37z" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.linkedin.com/in/', 'linkedin.com'),
	},
	{
		id: 'instagram',
		name: 'Instagram',
		defaultLabel: 'Instagram',
		urlPlaceholder: 'https://instagram.com/username or @username',
		urlPrefix: 'https://www.instagram.com/',
		category: 'social',
		iconBg: 'bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600 text-white',
		renderIcon: (cls = 'size-4') => (
			<svg
				className={cls}
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
				<path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
				<line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.instagram.com/', 'instagram.com'),
	},
	{
		id: 'facebook',
		name: 'Facebook',
		defaultLabel: 'Facebook',
		urlPlaceholder: 'https://facebook.com/page or username',
		urlPrefix: 'https://www.facebook.com/',
		category: 'social',
		iconBg: 'bg-[#1877f2] text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.facebook.com/', 'facebook.com'),
	},
	{
		id: 'x',
		name: 'X (Twitter)',
		defaultLabel: 'X (Twitter)',
		urlPlaceholder: 'https://x.com/username or @username',
		urlPrefix: 'https://x.com/',
		category: 'social',
		iconBg: 'bg-black text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://x.com/', 'x.com', 'twitter.com'),
	},
	{
		id: 'tiktok',
		name: 'TikTok',
		defaultLabel: 'TikTok',
		urlPlaceholder: 'https://tiktok.com/@username or @username',
		urlPrefix: 'https://www.tiktok.com/@',
		category: 'social',
		iconBg: 'bg-black text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.47 6.27 6.27 0 0 0 1.86-4.47V8.71a8.18 8.18 0 0 0 4.91 1.63V6.89a4.85 4.85 0 0 1-1-.2z" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.tiktok.com/@', 'tiktok.com'),
	},
	{
		id: 'youtube',
		name: 'YouTube',
		defaultLabel: 'YouTube',
		urlPlaceholder: 'https://youtube.com/@channel or channel',
		urlPrefix: 'https://www.youtube.com/@',
		category: 'media',
		iconBg: 'bg-[#ff0000] text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.youtube.com/@', 'youtube.com'),
	},
	{
		id: 'whatsapp',
		name: 'WhatsApp',
		defaultLabel: 'WhatsApp Chat',
		urlPlaceholder: 'https://wa.me/phonenumber or phone with country code',
		urlPrefix: 'https://wa.me/',
		category: 'messaging',
		iconBg: 'bg-[#25d366] text-white',
		renderIcon: (cls = 'size-4') => <MessageCircle className={cls} />,
		formatUrl: (raw) => {
			const cleaned = raw.trim();
			if (cleaned.startsWith('http://') || cleaned.startsWith('https://'))
				return normalizeUrl(cleaned);
			const digits = cleaned.replace(/[^\d+]/g, '').replace(/^\+/, '');
			return `https://wa.me/${digits}`;
		},
	},
	{
		id: 'telegram',
		name: 'Telegram',
		defaultLabel: 'Telegram',
		urlPlaceholder: 'https://t.me/username or @username',
		urlPrefix: 'https://t.me/',
		category: 'messaging',
		iconBg: 'bg-[#229ed9] text-white',
		renderIcon: (cls = 'size-4') => <Send className={cls} />,
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://t.me/', 't.me'),
	},
	{
		id: 'spotify',
		name: 'Spotify',
		defaultLabel: 'Listen on Spotify',
		urlPlaceholder: 'https://open.spotify.com/artist/…',
		category: 'media',
		iconBg: 'bg-[#1ed760] text-black',
		renderIcon: (cls = 'size-4') => <Disc className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://open.spotify.com/'),
	},
	{
		id: 'threads',
		name: 'Threads',
		defaultLabel: 'Threads',
		urlPlaceholder: 'https://threads.net/@username or @username',
		urlPrefix: 'https://www.threads.net/@',
		category: 'social',
		iconBg: 'bg-black text-white',
		renderIcon: () => <span className="font-black text-xs">@</span>,
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.threads.net/@', 'threads.net'),
	},
	{
		id: 'snapchat',
		name: 'Snapchat',
		defaultLabel: 'Snapchat',
		urlPlaceholder: 'https://snapchat.com/add/username or username',
		urlPrefix: 'https://www.snapchat.com/add/',
		category: 'social',
		iconBg: 'bg-[#fffc00] text-black',
		renderIcon: (cls = 'size-4') => <Sparkles className={cls} />,
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.snapchat.com/add/', 'snapchat.com'),
	},
	{
		id: 'pinterest',
		name: 'Pinterest',
		defaultLabel: 'Pinterest',
		urlPlaceholder: 'https://pinterest.com/username or username',
		urlPrefix: 'https://www.pinterest.com/',
		category: 'social',
		iconBg: 'bg-[#e60023] text-white',
		renderIcon: () => <span className="font-bold text-xs">P</span>,
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://www.pinterest.com/', 'pinterest.com'),
	},
	{
		id: 'github',
		name: 'GitHub',
		defaultLabel: 'GitHub',
		urlPlaceholder: 'https://github.com/username or username',
		urlPrefix: 'https://github.com/',
		category: 'dev',
		iconBg: 'bg-[#24292e] text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://github.com/', 'github.com'),
	},
	{
		id: 'gitlab',
		name: 'GitLab',
		defaultLabel: 'GitLab',
		urlPlaceholder: 'https://gitlab.com/username or username',
		urlPrefix: 'https://gitlab.com/',
		category: 'dev',
		iconBg: 'bg-[#fc6d26] text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="m23.6 9.6-1.5-4.6c-.2-.6-.9-1-1.5-.7-.4.2-.7.6-.7 1l-1.9 5.8H6.1L4.2 5.3c0-.4-.3-.8-.7-1-.6-.3-1.3.1-1.5.7L.5 9.6c-.2.6 0 1.3.5 1.7l11 8.1 11-8.1c.6-.4.8-1.1.6-1.7" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://gitlab.com/', 'gitlab.com'),
	},
	{
		id: 'dribbble',
		name: 'Dribbble',
		defaultLabel: 'Dribbble',
		urlPlaceholder: 'https://dribbble.com/username or username',
		urlPrefix: 'https://dribbble.com/',
		category: 'dev',
		iconBg: 'bg-[#ea4c89] text-white',
		renderIcon: (cls = 'size-4') => (
			<svg className={cls} viewBox="0 0 24 24" fill="currentColor">
				<path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm7.848 5.674a9.92 9.92 0 0 1 2.054 6.012c-.318-.063-2.617-.502-5.068-.135-.084-.19-.17-.384-.26-.576-1.32-2.83-2.836-5.06-2.97-5.257 2.658-.337 4.908.765 6.244 1.956zM12 2.095c.137.2.98 1.458 2.094 3.738-3.084 1.053-6.66 1.408-7.854 1.43 1.38-2.023 3.635-3.328 6.16-3.328zM4.148 8.875c.983-.02 4.417-.327 7.42-1.319.46.97.87 1.94 1.22 2.895-4.22 1.384-8.08 4.793-8.48 5.158-.69-1.38-1.09-2.94-1.09-4.6 0-.74.07-1.46.19-2.134h.74zm7.852 13.03c-2.33 0-4.44-.8-6.1-2.14.36-.37 3.8-3.52 7.84-4.8 1.15 2.9 1.63 5.48 1.73 6.05-.48.58-1.47.89-2.47.89zm4.72-2.14c-.11-.53-.56-2.92-1.63-5.65 2.29-.39 4.31.06 4.6.13-.37 2.37-1.46 4.45-2.97 5.52z" />
			</svg>
		),
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://dribbble.com/', 'dribbble.com'),
	},
	{
		id: 'discord',
		name: 'Discord',
		defaultLabel: 'Join our Discord',
		urlPlaceholder: 'https://discord.gg/inviteCode',
		category: 'messaging',
		iconBg: 'bg-[#5865f2] text-white',
		renderIcon: (cls = 'size-4') => <MessageCircle className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://discord.gg/'),
	},
	{
		id: 'soundcloud',
		name: 'SoundCloud',
		defaultLabel: 'SoundCloud',
		urlPlaceholder: 'https://soundcloud.com/artist',
		category: 'media',
		iconBg: 'bg-[#ff5500] text-white',
		renderIcon: (cls = 'size-4') => <Music className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://soundcloud.com/'),
	},
	{
		id: 'yelp',
		name: 'Yelp',
		defaultLabel: 'Review us on Yelp',
		urlPlaceholder: 'https://www.yelp.com/biz/…',
		category: 'reviews_food',
		iconBg: 'bg-[#d32323] text-white',
		renderIcon: (cls = 'size-4') => <Utensils className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://www.yelp.com/biz/'),
	},
	{
		id: 'tripadvisor',
		name: 'TripAdvisor',
		defaultLabel: 'TripAdvisor',
		urlPlaceholder: 'https://www.tripadvisor.com/…',
		category: 'reviews_food',
		iconBg: 'bg-[#34e0a1] text-black',
		renderIcon: (cls = 'size-4') => <Globe className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://www.tripadvisor.com/'),
	},
	{
		id: 'ubereats',
		name: 'Uber Eats',
		defaultLabel: 'Order on Uber Eats',
		urlPlaceholder: 'https://www.ubereats.com/store/…',
		category: 'reviews_food',
		iconBg: 'bg-[#06c167] text-white',
		renderIcon: (cls = 'size-4') => <ShoppingBag className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://www.ubereats.com/'),
	},
	{
		id: 'doordash',
		name: 'DoorDash',
		defaultLabel: 'Order on DoorDash',
		urlPlaceholder: 'https://www.doordash.com/store/…',
		category: 'reviews_food',
		iconBg: 'bg-[#ff3008] text-white',
		renderIcon: (cls = 'size-4') => <ShoppingBag className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://www.doordash.com/'),
	},
	{
		id: 'messenger',
		name: 'Messenger',
		defaultLabel: 'Message us on Facebook',
		urlPlaceholder: 'https://m.me/username or username',
		urlPrefix: 'https://m.me/',
		category: 'messaging',
		iconBg: 'bg-[#0084ff] text-white',
		renderIcon: (cls = 'size-4') => <MessageCircle className={cls} />,
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://m.me/', 'm.me'),
	},
	{
		id: 'twitch',
		name: 'Twitch',
		defaultLabel: 'Watch on Twitch',
		urlPlaceholder: 'https://twitch.tv/channel or channel',
		urlPrefix: 'https://twitch.tv/',
		category: 'media',
		iconBg: 'bg-[#9146ff] text-white',
		renderIcon: (cls = 'size-4') => <Radio className={cls} />,
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://twitch.tv/', 'twitch.tv'),
	},
	{
		id: 'reddit',
		name: 'Reddit',
		defaultLabel: 'Reddit Community',
		urlPlaceholder: 'https://reddit.com/r/community or r/community',
		category: 'social',
		iconBg: 'bg-[#ff4500] text-white',
		renderIcon: (cls = 'size-4') => <Share2 className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw, 'https://reddit.com/'),
	},
	{
		id: 'medium',
		name: 'Medium',
		defaultLabel: 'Read on Medium',
		urlPlaceholder: 'https://medium.com/@username or @username',
		category: 'social',
		iconBg: 'bg-black text-white',
		renderIcon: () => <span className="font-serif font-black text-xs">M</span>,
		formatUrl: (raw) => formatPlatformUrl(raw, 'https://medium.com/@', 'medium.com'),
	},
	{
		id: 'custom',
		name: 'Custom Link',
		defaultLabel: 'Visit Link',
		urlPlaceholder: 'https://anylink.com',
		category: 'other',
		iconBg: 'bg-slate-700 text-white',
		renderIcon: (cls = 'size-4') => <Link2 className={cls} />,
		formatUrl: (raw) => normalizeUrl(raw),
	},
];

export const PLATFORMS_BY_ID: Record<string, SocialPlatformMeta> = Object.fromEntries(
	SOCIAL_PLATFORMS_REGISTRY.map((p) => [p.id, p]),
);

function normalizeUrl(raw: string, defaultPrefix = 'https://'): string {
	const trimmed = raw.trim();
	if (!trimmed) return '';
	if (trimmed.startsWith('https://')) return trimmed;
	if (trimmed.startsWith('http://')) return `https://${trimmed.slice(7)}`;
	return `${defaultPrefix}${trimmed.replace(/^\/+/, '')}`;
}

function formatPlatformUrl(raw: string, urlPrefix: string, ...domains: string[]): string {
	const trimmed = raw.trim();
	if (!trimmed) return '';
	// If it already has http:// or https://
	if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
		return normalizeUrl(trimmed);
	}
	// If it starts with the domain directly (e.g. instagram.com/user)
	for (const domain of domains) {
		if (trimmed.toLowerCase().startsWith(domain)) {
			return `https://${trimmed}`;
		}
	}
	// It's a username / handle (e.g. @larrysleez or larrysleez)
	const cleanHandle = trimmed.replace(/^@+/, '');
	return `${urlPrefix}${cleanHandle}`;
}
